import React, { useState, useEffect } from 'react';
import { 
    PlusIcon, 
    PencilIcon, 
    TrashIcon, 
    MagnifyingGlassIcon,
    BuildingOfficeIcon,
    MapPinIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import { apiUrl } from '../../utils/api';

interface Building {
    _id: string;
    nombre: string;
    direccion: string;
    ciudad: string;
    codigoPostal: string;
    pais: string;
    descripcion?: string;
    tipo: 'hotel' | 'apartamento' | 'residencial' | 'comercial';
    numeroPisos: number;
    amenidades: string[];
    activo: boolean;
    linkDocumentos?: string;
    linkFinanciero?: string;
    ocupacion?: number;
    administrador?: {
        _id: string;
        nombre: string;
        apellido: string;
        email: string;
    };
    createdAt: string;
    updatedAt: string;
}

interface BuildingFormData {
    nombre: string;
    direccion: string;
    ciudad: string;
    codigoPostal: string;
    pais: string;
    descripcion: string;
    tipo: 'hotel' | 'apartamento' | 'residencial' | 'comercial';
    numeroPisos: number;
    amenidades: string;
    administrador: string;
    linkDocumentos: string;
    linkFinanciero: string;
    ocupacion: number;
}

const BuildingManagement: React.FC = () => {
    const [buildings, setBuildings] = useState<Building[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<string>('');
    const [filterActivo, setFilterActivo] = useState<string>('');
    const [showModal, setShowModal] = useState(false);
    const [editingBuilding, setEditingBuilding] = useState<Building | null>(null);
    const [syncingBuilding, setSyncingBuilding] = useState<string | null>(null);
    const [formData, setFormData] = useState<BuildingFormData>({
        nombre: '',
        direccion: '',
        ciudad: '',
        codigoPostal: '',
        pais: 'España',
        descripcion: '',
        tipo: 'hotel',
        numeroPisos: 1,
        amenidades: '',
        administrador: '',
        linkDocumentos: '',
        linkFinanciero: '',
        ocupacion: 0
    });

    useEffect(() => {
        fetchBuildings();
    }, []);

    const fetchBuildings = async () => {
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filterType) params.append('tipo', filterType);
            if (filterActivo) params.append('activo', filterActivo);
            
            const response = await fetch(apiUrl(`/api/buildings?${params}`), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setBuildings(data.data.buildings);
            } else {
                console.error('Error al obtener edificios');
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            const token = localStorage.getItem('token');
            const url = editingBuilding ? apiUrl(`/api/buildings/${editingBuilding._id}`) : apiUrl('/api/buildings');
            const method = editingBuilding ? 'PUT' : 'POST';
            
            // Procesar amenidades: convertir string a array
            const dataToSend = {
                ...formData,
                amenidades: formData.amenidades ? formData.amenidades.split(',').map(a => a.trim()).filter(a => a) : []
            };
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dataToSend)
            });

            if (response.ok) {
                await fetchBuildings();
                setShowModal(false);
                setEditingBuilding(null);
                setFormData({
                    nombre: '',
                    direccion: '',
                    ciudad: '',
                    codigoPostal: '',
                    pais: 'España',
                    descripcion: '',
                    tipo: 'hotel',
                    numeroPisos: 1,
                    amenidades: '',
                    administrador: '',
                    linkDocumentos: '',
                    linkFinanciero: '',
                    ocupacion: 0
                });
            } else {
                const error = await response.json();
                alert(error.message || 'Error al guardar edificio');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al guardar edificio');
        }
    };

    const handleEdit = (building: Building) => {
        setEditingBuilding(building);
        setFormData({
            nombre: building.nombre,
            direccion: building.direccion,
            ciudad: building.ciudad,
            codigoPostal: building.codigoPostal,
            pais: building.pais,
            descripcion: building.descripcion || '',
            tipo: building.tipo,
            numeroPisos: building.numeroPisos,
            amenidades: Array.isArray(building.amenidades) ? building.amenidades.join(', ') : building.amenidades || '',
            administrador: building.administrador?._id || '',
            linkDocumentos: building.linkDocumentos || '',
            linkFinanciero: building.linkFinanciero || '',
            ocupacion: building.ocupacion !== undefined && building.ocupacion !== null ? building.ocupacion : 0
        });
        setShowModal(true);
    };

    const handleDelete = async (buildingId: string) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar este edificio?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl(`/api/buildings/${buildingId}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                await fetchBuildings();
            } else {
                const error = await response.json();
                alert(error.message || 'Error al eliminar edificio');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al eliminar edificio');
        }
    };

    const handleSyncDocuments = async (building: Building) => {
        if (!building.linkDocumentos) {
            alert('Este edificio no tiene enlace de documentos configurado');
            return;
        }

        setSyncingBuilding(building._id);
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl(`/api/buildings/${building._id}/sync-documents`), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    linkDocumentos: building.linkDocumentos
                })
            });

            if (response.ok) {
                const result = await response.json();
                console.log('Archivos encontrados:', result.data.files);
                alert(`Sincronización completada. Se encontraron ${result.data.files.length} archivos. Revisa la consola para más detalles.`);
            } else {
                const error = await response.json();
                alert(error.message || 'Error al sincronizar documentos');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al sincronizar documentos');
        } finally {
            setSyncingBuilding(null);
        }
    };

    const getTipoLabel = (tipo: string) => {
        const labels: { [key: string]: string } = {
            'hotel': 'Hotel',
            'apartamento': 'Apartamento',
            'residencial': 'Residencial',
            'comercial': 'Comercial'
        };
        return labels[tipo] || tipo;
    };

    const filteredBuildings = buildings.filter(building => {
        const matchesSearch = !searchTerm || 
            building.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            building.ciudad.toLowerCase().includes(searchTerm.toLowerCase()) ||
            building.direccion.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesType = !filterType || building.tipo === filterType;
        const matchesActivo = !filterActivo || building.activo.toString() === filterActivo;
        
        return matchesSearch && matchesType && matchesActivo;
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
                    <h2 className="text-2xl font-semibold text-gray-600">Gestión de Edificios</h2>
                    <p className="text-gray-600 mt-2">Administra edificios y conjuntos del hotel</p>
                </div>
                <button
                    onClick={() => {
                        setEditingBuilding(null);
                        setFormData({
                            nombre: '',
                            direccion: '',
                            ciudad: '',
                            codigoPostal: '',
                            pais: 'España',
                            descripcion: '',
                            tipo: 'hotel',
                            numeroPisos: 1,
                            amenidades: '',
                            administrador: '',
                            linkDocumentos: '',
                        linkFinanciero: '',
                        ocupacion: 0
                        });
                        setShowModal(true);
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2"
                >
                    <PlusIcon className="h-4 w-4" />
                    <span>Nuevo Edificio</span>
                </button>
            </div>

            {/* Filtros y Búsqueda */}
            <div className="bg-white p-4 rounded-lg shadow-md border">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar por nombre, ciudad o dirección..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los tipos</option>
                            <option value="hotel">Hotel</option>
                            <option value="apartamento">Apartamento</option>
                            <option value="residencial">Residencial</option>
                            <option value="comercial">Comercial</option>
                        </select>
                        <select
                            value={filterActivo}
                            onChange={(e) => setFilterActivo(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los estados</option>
                            <option value="true">Activo</option>
                            <option value="false">Inactivo</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Tabla de Edificios */}
            <div className="bg-white rounded-lg shadow-md border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Edificio
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Ubicación
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Tipo
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Pisos
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    % Ocupación
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Administrador
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Documentos
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Financiero
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredBuildings.map((building) => (
                                <tr key={building._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <BuildingOfficeIcon className="h-8 w-8 text-blue-600 mr-3" />
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {building.nombre}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {building.descripcion || 'Sin descripción'}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
                                            <div>
                                                <div className="text-sm text-gray-900">
                                                    {building.direccion}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {building.ciudad}, {building.codigoPostal}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {getTipoLabel(building.tipo)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {building.numeroPisos}
                                    </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {building.ocupacion !== undefined && building.ocupacion !== null
                                        ? `${building.ocupacion.toFixed(2)}%`
                                        : 'N/A'}
                                </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            building.activo 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-red-100 text-red-800'
                                        }`}>
                                            {building.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {building.administrador 
                                            ? `${building.administrador.nombre} ${building.administrador.apellido}`
                                            : 'Sin asignar'
                                        }
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {building.linkDocumentos ? (
                                            <a 
                                                href={building.linkDocumentos} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 underline"
                                            >
                                                Ver Documentos
                                            </a>
                                        ) : (
                                            <span className="text-gray-400">Sin enlace</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {building.linkFinanciero ? (
                                            <a 
                                                href={building.linkFinanciero} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-green-600 hover:text-green-800 underline"
                                            >
                                                Ver Financiero
                                            </a>
                                        ) : (
                                            <span className="text-gray-400">Sin enlace</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-2">
                                            {building.linkDocumentos && (
                                                <button
                                                    onClick={() => handleSyncDocuments(building)}
                                                    disabled={syncingBuilding === building._id}
                                                    className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                                    title="Sincronizar documentos"
                                                >
                                                    <ArrowPathIcon className={`h-4 w-4 ${syncingBuilding === building._id ? 'animate-spin' : ''}`} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleEdit(building)}
                                                className="text-blue-600 hover:text-blue-900"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(building._id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de Edificio */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                            {editingBuilding ? 'Editar Edificio' : 'Nuevo Edificio'}
                        </h2>
                        
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre del Edificio
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.nombre}
                                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tipo
                                    </label>
                                    <select
                                        required
                                        value={formData.tipo}
                                        onChange={(e) => setFormData({ ...formData, tipo: e.target.value as any })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="hotel">Hotel</option>
                                        <option value="apartamento">Apartamento</option>
                                        <option value="residencial">Residencial</option>
                                        <option value="comercial">Comercial</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Dirección
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.direccion}
                                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ciudad
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.ciudad}
                                        onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Código Postal
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.codigoPostal}
                                        onChange={(e) => setFormData({ ...formData, codigoPostal: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        País
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.pais}
                                        onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Descripción
                                </label>
                                <textarea
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Enlace de Documentos
                                </label>
                                <input
                                    type="url"
                                    value={formData.linkDocumentos}
                                    onChange={(e) => setFormData({ ...formData, linkDocumentos: e.target.value })}
                                    placeholder="https://drive.google.com/..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Enlace de Google Drive donde se almacenan los documentos del edificio
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Enlace Financiero
                                </label>
                                <input
                                    type="url"
                                    value={formData.linkFinanciero}
                                    onChange={(e) => setFormData({ ...formData, linkFinanciero: e.target.value })}
                                    placeholder="https://docs.google.com/spreadsheets/..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Enlace a información financiera del edificio (Google Sheets, Excel, etc.)
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    % de Ocupación
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={formData.ocupacion}
                                    onChange={(e) => setFormData({ ...formData, ocupacion: parseFloat(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Número de Pisos
                                </label>
                                <input
                                    type="number"
                                    required
                                    min="1"
                                    value={formData.numeroPisos}
                                    onChange={(e) => setFormData({ ...formData, numeroPisos: parseInt(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Amenidades (separadas por comas)
                                </label>
                                <input
                                    type="text"
                                    value={formData.amenidades}
                                    onChange={(e) => setFormData({ 
                                        ...formData, 
                                        amenidades: e.target.value 
                                    })}
                                    placeholder="Piscina, Gimnasio, Parking, WiFi"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                    {editingBuilding ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BuildingManagement;
